/**
 * React Query hook for L3 map custom views (ADR-MV-05).
 * Flat key: customerQueryKeys.mapCustomViews(customerKey) — peer salesPointContacts.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import { useRuntimeConfig } from '../../shared/providers/RuntimeConfigProvider.js';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { ACCOUNT_ME_STALE_MS } from '../../shared/session/fetchPlatformMe.js';
import { isGloballySignedInChrome } from '../hub/customerHubPaths.js';
import { listMapCustomViews } from './mapViewsApi.js';
import type { MapCustomViewDTO } from './mapViewsTypes.js';

const MAP_VIEWS_QUERY_OPTIONS = {
  staleTime: ACCOUNT_ME_STALE_MS,
  refetchOnMount: false as const,
  refetchOnWindowFocus: false,
};

/**
 * GET map custom views via L3 absolute `/api/v1/...` + credentials include.
 * Enablement matches global chrome — `/me`-established session memory only
 * ({@link isGloballySignedInChrome}). BAN jwt LS alone.
 *
 * G2 consumers gating restore on first load must use `isPending` (not `isLoading`):
 * offline/paused first fetch can be `isPending && !isFetching` → `isLoading === false`.
 */
export function useCustomerMapViews(
  enabled = true,
): UseQueryResult<MapCustomViewDTO[], Error> {
  const { apiBaseUrl } = useRuntimeConfig();
  const { session } = useCustomerSession();
  const signedInChrome = isGloballySignedInChrome({ session });
  const customerKey =
    session?.customerId ?? (signedInChrome ? 'session' : -1);

  return useQuery({
    queryKey: customerQueryKeys.mapCustomViews(customerKey),
    queryFn: ({ signal }) =>
      listMapCustomViews({
        apiBaseUrl,
        signal,
      }),
    enabled: enabled && signedInChrome,
    ...MAP_VIEWS_QUERY_OPTIONS,
  });
}
